import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import { useTranslation } from "react-i18next";
import {
  AddShoppingCart,
  LocalShipping,
  MonetizationOn,
  Widgets,
} from "@mui/icons-material";

export default function Features() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);
  const { t } = useTranslation();

  const handleItemClick = (index) => {
    setSelectedItemIndex(index);
  };

  const items = [
    {
      icon: <MonetizationOn />,
      title: t("landingpage.section1title"),
      description: t("landingpage.section1text"),
      imageLight:
        'url("https://storage.googleapis.com/pifa.se/public/vintage.jpeg")',
      imageDark: `url("https://storage.googleapis.com/pifa.se/public/vintage.jpeg")`,
      url: "https://storage.googleapis.com/pifa.se/public/vintage.jpeg",
    },
    {
      icon: <AddShoppingCart />,
      title: t("landingpage.section2title"),
      description: t("landingpage.section2text"),
      imageLight:
        'url("https://storage.googleapis.com/pifa.se/public/margoz.jpeg")',
      imageDark: `url("https://storage.googleapis.com/pifa.se/public/margoz.jpeg")`,
      url: "https://storage.googleapis.com/pifa.se/public/margoz.jpeg",
    },
    {
      icon: <Widgets />,
      title: t("landingpage.section3title"),
      description: t("landingpage.section3text"),
      imageLight:
        'url("https://storage.googleapis.com/pifa.se/public/ekebacken.jpeg")',
      imageDark: `url("https://storage.googleapis.com/pifa.se/public/ekebacken.jpeg")`,
      url: "https://storage.googleapis.com/pifa.se/public/ekebacken.jpeg",
    },
    {
      icon: <LocalShipping />,
      title: t("landingpage.section4title"),
      description: t("landingpage.section4text"),
      imageLight:
        'url("https://storage.googleapis.com/pifa.se/public/ruchey.jpeg")',
      imageDark: `url("https://storage.googleapis.com/pifa.se/public/ruchey.jpeg")`,
      url: "https://storage.googleapis.com/pifa.se/public/ruchey.jpeg",
    },
    {
      icon: <DevicesRoundedIcon />,
      title: t("landingpage.section5title"),
      description: t("landingpage.section5text"),
      imageLight:
        'url("https://storage.googleapis.com/pifa.se/public/kmais.jpeg")',
      imageDark: `url("https://storage.googleapis.com/pifa.se/public/kmais.jpeg")`,
      url: "https://storage.googleapis.com/pifa.se/public/kmais.jpeg",
    },
  ];

  return (
    <Container id="features" sx={{ py: { xs: 4, sm: 12 } }}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <div>
            {/* Använd h5 istället för body1 med hårdkodad fontSize */}
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                mb: 1,
                textAlign: "center",
              }}
            >
              {t("landingpage.featurestitle")}
            </Typography>
            {/* Använd h4 istället för body1 med hårdkodad fontSize */}
            <Typography
              variant="h4"
              color="text.primary"
              sx={{
                mb: { xs: 2, sm: 4 },
                fontWeight: 400,
                lineHeight: 1.6,
                textAlign: "center",
                fontSize: "x-large",
                paddingBlock: "10px",
              }}
            >
              {t("landingpage.featurestext")}
            </Typography>
          </div>

          {/* Cards */}
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
            spacing={2}
            useFlexGap
            sx={{ width: "100%" }}
          >
            {items.map(({ icon, title, description }, index) => (
              <Card
                key={index}
                component={Button}
                onClick={() => handleItemClick(index)}
                sx={{
                  p: 3,
                  height: "fit-content",
                  width: "100%",
                  background: "none",
                  borderRadius: 3,
                  border: 2,
                  borderColor:
                    selectedItemIndex === index
                      ? "primary.main"
                      : (theme) =>
                          theme.palette.mode === "light"
                            ? "grey.200"
                            : "grey.700",
                  backgroundColor:
                    selectedItemIndex === index
                      ? (theme) =>
                          theme.palette.mode === "light"
                            ? "primary.light"
                            : "primary.dark"
                      : "transparent",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: selectedItemIndex === index ? 8 : 4,
                    borderColor: "primary.main",
                    backgroundColor:
                      selectedItemIndex === index
                        ? (theme) =>
                            theme.palette.mode === "light"
                              ? "primary.light"
                              : "primary.dark"
                        : (theme) =>
                            theme.palette.mode === "light"
                              ? "grey.50"
                              : "grey.900",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    textAlign: "left",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { md: "center" },
                    gap: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      color:
                        selectedItemIndex === index
                          ? "primary.main"
                          : "text.secondary",
                      transition: "color 0.3s ease",
                      fontSize: "2rem",
                    }}
                  >
                    {icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      color="text.primary"
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mb: 0.5 }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {description}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        </Grid>

        {/* Bildsektion */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", sm: "flex" },
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "600px",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: 8,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: (theme) =>
                  `linear-gradient(45deg, ${theme.palette.primary.main}20, transparent)`,
                zIndex: 1,
                transition: "opacity 0.3s ease",
              },
            }}
          >
            <img
              src={items[selectedItemIndex].url}
              alt={items[selectedItemIndex].title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.5s ease",
                transform: "scale(1.02)",
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
